import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { CreditCard, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import api, { formatApiError } from "@/lib/api";

const BADGE = { active: "bg-green-500/15 text-green-400", cancelled: "bg-amber-500/15 text-amber-400", not_subscribed: "bg-zinc-700 text-zinc-300", past_due: "bg-[#E53E3E]/15 text-[#E53E3E]", expired: "bg-[#E53E3E]/15 text-[#E53E3E]", refunded: "bg-zinc-700 text-zinc-300" };

export function MemberBilling() {
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/subscription"); setSub(data); } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!sub) return null;

  const sym = sub.symbol || "$";
  const price = Number(sub.amount ?? sub.price ?? 0);
  const fmt = (n) => `${sym}${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const configured = sub.paystack_configured;
  const needsPay = ["not_subscribed", "expired", "refunded", "past_due"].includes(sub.status);

  const startCheckout = async () => {
    setBusy(true);
    try {
      const callback_url = `${window.location.origin}/payment/callback`;
      const { data } = await api.post("/subscription/initialize", { callback_url });
      window.location.href = data.authorization_url; // full redirect to Paystack
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };
  const sandboxActivate = async () => {
    setBusy(true);
    try { await api.post("/subscription/subscribe", { payment_token: "mock_tok_valid" }); toast.success("Membership active (sandbox)."); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const subscribe = () => (configured ? startCheckout() : sandboxActivate());
  const cancel = async () => {
    setBusy(true);
    try { await api.post("/subscription/cancel"); toast.success("Will not renew; access kept until period end."); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-6" data-testid="member-billing">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-bold uppercase tracking-tight flex items-center gap-2"><CreditCard className="w-5 h-5 text-[#E53E3E]" /> Membership</h3>
        <Badge className={BADGE[sub.status] || "bg-zinc-700"} data-testid="subscription-status">{sub.status.replace("_", " ")}</Badge>
      </div>
      <p className="mt-3 font-display text-3xl font-black" data-testid="membership-price">{fmt(price)}<span className="text-sm text-zinc-500 font-sans font-normal">/month · {sub.currency}</span></p>
      {sub.charge_note && (
        <p className="mt-1 text-xs text-zinc-500" data-testid="charge-note">{sub.charge_note}</p>
      )}
      {(sub.status === "active" || sub.status === "cancelled") && sub.current_period_start && (
        <p className="text-xs text-zinc-500 mt-1" data-testid="membership-started">Started {new Date(sub.current_period_start).toLocaleDateString()}</p>
      )}
      {(sub.status === "active" || sub.status === "cancelled") && sub.current_period_end && (
        <p className="text-xs text-zinc-500 mt-0.5" data-testid="membership-renews">{sub.cancel_at_period_end ? "Access until" : "Renews"} {new Date(sub.current_period_end).toLocaleDateString()}</p>
      )}
      <p className="mt-3 text-sm text-zinc-400">Covers your FitLynk membership and your accountability partner relationship.</p>

      {!configured && needsPay && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-300" data-testid="paystack-config-required">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Live payments require Paystack configuration. You can activate a <strong>sandbox</strong> membership below to preview the experience.</span>
        </div>
      )}

      {needsPay ? (
        <Button onClick={subscribe} disabled={busy} className="mt-4 w-full bg-[#E53E3E] hover:bg-[#C53030] rounded-full font-semibold h-11" data-testid="subscribe-button">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : configured ? `Subscribe with Paystack · ${fmt(price)}` : "Activate Sandbox Membership"}
        </Button>
      ) : sub.status === "active" ? (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" className="mt-4 w-full border-zinc-700 bg-transparent hover:bg-zinc-800 rounded-full" data-testid="cancel-subscription-button">Cancel Membership</Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="bg-[#0A0A0B] border-zinc-800">
            <AlertDialogHeader><AlertDialogTitle>Cancel membership?</AlertDialogTitle><AlertDialogDescription className="text-zinc-400">You'll keep access until the end of your current billing period and won't be charged again.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel className="bg-transparent border-zinc-700 hover:bg-zinc-800">Keep</AlertDialogCancel><AlertDialogAction onClick={cancel} className="bg-[#E53E3E] hover:bg-[#C53030]" data-testid="confirm-cancel-subscription">Cancel Membership</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ) : (
        <p className="mt-4 text-sm text-amber-400 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Access retained until period end.</p>
      )}
      <p className="mt-3 text-[11px] text-zinc-600">{configured ? "Secure checkout powered by Paystack." : "Paystack not configured — sandbox mode for testing only."}</p>
    </div>
  );
}
